import React, { useState, useContext, useRef } from 'react';
import { Formik } from 'formik';
import { GenericModal, MaterialInputField, PhoneInputField } from 'components';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Button, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import dayjs from 'dayjs';
import Autocomplete from 'react-google-autocomplete';
import {
  GenderList,
  constants,
  countriesList,
  emergencyRelationshipList,
  ENUMS,
} from 'common';
import { nationality } from 'common/nationality';
import { civilStatus } from 'common/civilstatus';
import {
  signUpStep1Schema,
  signUpStep2Schema,
  signUpStep4Schema,
  personalInfoModalSchema,
} from 'common/schemas';
import { saveUserProfile } from 'services';
import {
  getProfile,
  setSecondaryProfile,
  getSecondaryProfile,
} from 'utilities';
import { CircularProgress } from '@mui/material';
import { InputField } from 'components/input-field';
import { ApplicationContext } from 'shared';
import './styles.scss';

const { fieldTypeEnums, placeholders } = constants;

export const converDateFormate = (dateString) => {
  const originalDate = new Date(dateString);
  const formattedDate = originalDate.toLocaleDateString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return formattedDate;
};

// Personal Information Modal
export const PersonalInfoModal = ({ show, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const formikRef = useRef(null);
  const userData = getSecondaryProfile();
  // When insurance is on, name + DOB are collected during signup, so hide those
  // fields here (their values are still pre-filled and submitted).
  const insuranceEnabled = process.env.REACT_APP_INSURANCE_ENABLED === "true";

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const profileId = getProfile()?.uid;
      let date = converDateFormate(values.dob);

      const updatedData = {
        firstName: values.firstName,
        lastName: values.lastName,
        birthDate: date,
        nationality: values.nationality,
        gender: values.gender,
        ssn: values.socialNum,
        organDonation: values.organDonar,
        relationShip: values.relationShip,
      };

      await saveUserProfile({ profileId, ...updatedData });

      const updatedProfile = { ...userData, ...updatedData };
      setSecondaryProfile(updatedProfile);
      setLoading(false);
      onComplete();
    } catch (error) {
      console.error('Error saving personal info:', error);
      setLoading(false);
    }
  };

  const handleModalSubmit = () => {
    console.log('inside handle submit');

    if (formikRef.current) {
      formikRef.current.setTouched({
        firstName: true,
        lastName: true,
        dob: true,
        gender: true,
        nationality: true,
        socialNum: true,
      });
      formikRef.current.handleSubmit();
    }
  };

  return (
    <GenericModal
      show={show}
      type={ENUMS.MODAL_TYPES.INFO}
      title='Complete Your Personal Information'
      body={
        <Formik
          innerRef={formikRef}
          initialValues={{
            firstName: userData?.firstName || '',
            lastName: userData?.lastName || '',
            dob: userData?.birthDate || null,
            socialNum: userData?.ssn || '',
            organDonar: userData?.organDonation || '',
            relationShip: userData?.relationShip || '',
          }}
          validateOnMount={true}
          onSubmit={handleSubmit}
          validationSchema={personalInfoModalSchema}
        >
          {({
            handleChange,
            handleBlur,
            handleSubmit,
            touched,
            errors,
            values,
            setFieldValue,
            setTouched,
            isValid,
          }) => (
            <div className='post-signup-modal-content'>
              <Grid container spacing={0.7}>
                {!insuranceEnabled && (
                  <>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className='mb'>
                    <MaterialInputField
                      name={'firstName'}
                      id={'firstName'}
                      error={touched.firstName && errors.firstName}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      label={'First Name *'}
                      value={values.firstName}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <MaterialInputField
                    name={'lastName'}
                    id={'lastName'}
                    error={touched.lastName && errors.lastName}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    label={'Last Name *'}
                    value={values.lastName}
                  />
                </Grid>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className='full-width-datepicker'>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <MobileDatePicker
                        label='DOB *'
                        value={values.dob ? dayjs(values.dob) : null}
                        onChange={(newValue) => {
                          setFieldValue('dob', newValue ? newValue.toDate() : null);
                        }}
                        maxDate={dayjs()}
                        format='DD/MM/YYYY'
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: {
                              boxShadow: 'none',
                              backgroundColor: '#fafafa',
                              borderRadius: '10px',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                              },
                              '& .MuiOutlinedInput-notchedOutline': { border: 0 },
                              '& .MuiInputBase-input': {
                                fontSize: '16px',
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                    <div className='error-input-container'>
                      {errors.dob && touched.dob ? (
                        <p className='form-error'>{errors.dob}</p>
                      ) : null}
                    </div>
                  </div>
                </Grid>
                  </>
                )}
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className='mb'>
                    <Box sx={{ minWidth: 120 }}>
                      <FormControl fullWidth>
                        <InputLabel id='gender-label'>Gender *</InputLabel>
                        <Select
                          sx={{
                            boxShadow: 'none',
                            '.MuiOutlinedInput-notchedOutline': { border: 0 },
                          }}
                          labelId='gender-label'
                          id='gender-select'
                          value={values.gender}
                          label='gender'
                          onChange={handleChange('gender')}
                        >
                          {GenderList.map(({ value, label }) => {
                            return (
                              <MenuItem key={value} value={value}>
                                {label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Box>
                    <div className='error-input-container'>
                      {errors.gender && touched.gender ? (
                        <p className='form-error'>{errors.gender}</p>
                      ) : null}
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className='mb-1'>
                    <Box sx={{ minWidth: 120 }}>
                      <FormControl fullWidth>
                        <InputLabel id='nationality-label'>
                          Nationality *
                        </InputLabel>
                        <Select
                          sx={{
                            boxShadow: 'none',
                            '.MuiOutlinedInput-notchedOutline': { border: 0 },
                          }}
                          labelId='nationality-label'
                          id='nationality-select'
                          value={values.nationality}
                          label='Select a nationality'
                          onChange={handleChange('nationality')}
                        >
                          {nationality.map((nationality, index) => (
                            <MenuItem key={index} value={nationality}>
                              {nationality}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <div className='error-input-container'>
                      {errors.nationality && touched.nationality ? (
                        <p className='form-error'>{errors.nationality}</p>
                      ) : null}
                    </div>
                  </div>
                </Grid>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className='mb-1'>
                    <Box sx={{ minWidth: 120 }}>
                      <FormControl fullWidth>
                        <InputLabel id='civil-status-label'>
                          Civil Status
                        </InputLabel>
                        <Select
                          sx={{
                            boxShadow: 'none',
                            '.MuiOutlinedInput-notchedOutline': { border: 0 },
                          }}
                          labelId='civil-status-label'
                          id='civil-status-select'
                          value={values.relationShip}
                          label='Civil Status'
                          onChange={handleChange('relationShip')}
                        >
                          {civilStatus.map((relationShip, index) => (
                            <MenuItem key={index} value={relationShip}>
                              {relationShip}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </div>
                </Grid>
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className='mb'>
                    <MaterialInputField
                      name={'socialNum'}
                      id={'socialNum'}
                      error={touched.socialNum && errors.socialNum}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      label={'National ID number'}
                      value={values.socialNum}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} xl={12} lg={12} md={12}>
                  <label style={{ paddingLeft: 10, paddingTop: 10 }}>
                    Would you like to be an organ donor? (optional)
                  </label>
                </Grid>
                <Grid item xs={12} xl={12} lg={12} md={12}>
                  <Box sx={{ minWidth: 120 }}>
                    <FormControl fullWidth>
                      <InputLabel id='organ-donor-label'>
                        Organ Donor
                      </InputLabel>
                      <Select
                        sx={{
                          boxShadow: 'none',
                          '.MuiOutlinedInput-notchedOutline': { border: 0 },
                        }}
                        labelId='organ-donor-label'
                        id='organ-donor-select'
                        value={values.organDonar}
                        label='Organ Donor'
                        onChange={handleChange('organDonar')}
                      >
                        <MenuItem value={true}>Yes</MenuItem>
                        <MenuItem value={false}>No</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>
            </div>
          )}
        </Formik>
      }
      buttonText={loading ? null : 'Submit'}
      btnLoader={loading}
      handleClose={handleModalSubmit}
      customModalClass='post-signup-modal'
    />
  );
};

// Address Modal
export const AddressModal = ({ show, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const formikRef = useRef(null);
  const userData = getSecondaryProfile();

  const convertArrayToString = (data) => {
    const joined = data.join();
    let newAddress = joined.replace(/,/g, ' ');
    return newAddress;
  };

  const AddressSeprateHandler = (data, setFieldValue, values) => {
    let address = [];
    let city = [];
    let state = [];
    let postal = [];
    let country = [];
    data.map((dataItem) => {
      dataItem.types.map((type) => {
        if (type === fieldTypeEnums.streetNumber) {
          address.push(dataItem.long_name);
        } else if (type === fieldTypeEnums.route) {
          address.push(dataItem.long_name);
        } else if (type === fieldTypeEnums.naibhour) {
          address.push(dataItem.long_name);
        } else if (type === fieldTypeEnums.locality) {
          city.push(dataItem.long_name);
        } else if (type === fieldTypeEnums.admin) {
          state.push(dataItem.long_name);
        } else if (type === fieldTypeEnums.country) {
          country.push(dataItem.long_name);
        } else if (type === fieldTypeEnums.code) {
          postal.push(dataItem.long_name);
        }
      });
    });
    setFieldValue('address', values);
    setFieldValue('city', convertArrayToString(city));
    setFieldValue('state', convertArrayToString(state));
    setFieldValue('zipCode', convertArrayToString(postal));
    setFieldValue('country', convertArrayToString(country));
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const profileId = getProfile()?.uid;

      const updatedData = {
        postalAddress: [
          {
            city: values.city,
            state: values.state,
            info: values.address,
            zipCode: values.zipCode,
            country: values.country,
          },
        ],
      };

      await saveUserProfile({
        profileId,
        ...updatedData,
      });

      const updatedProfile = { ...userData, ...updatedData };
      setSecondaryProfile(updatedProfile);
      setLoading(false);
      onComplete();
    } catch (error) {
      console.error('Error saving address:', error);
      setLoading(false);
    }
  };

  const handleModalSubmit = () => {
    if (formikRef.current) {
      formikRef.current.setTouched({
        address: true,
        city: true,
        state: true,
        zipCode: true,
        country: true,
      });
      formikRef.current.handleSubmit();
    }
  };

  return (
    <GenericModal
      show={show}
      type={ENUMS.MODAL_TYPES.INFO}
      title='Complete Your Address Information'
      body={
        <Formik
          innerRef={formikRef}
          initialValues={{
            address: userData?.postalAddress?.[0]?.info || '',
            city: userData?.postalAddress?.[0]?.city || '',
            state: userData?.postalAddress?.[0]?.state || '',
            zipCode: userData?.postalAddress?.[0]?.zipCode || '',
            country: userData?.postalAddress?.[0]?.country || '',
          }}
          validateOnMount={true}
          onSubmit={handleSubmit}
          validationSchema={signUpStep2Schema}
          enableReinitialize
        >
          {({
            handleChange,
            handleBlur,
            touched,
            errors,
            values,
            setFieldValue,
          }) => (
            <div className='post-signup-modal-content'>
              <Grid container spacing={0.7}>
                {/* Address Field */}
                <Grid item xs={12} xl={12} lg={12} md={12}>
                  <div className='mb'>
                    <MaterialInputField
                      name='address'
                      id='address'
                      label='Address *'
                      error={touched.address && errors.address}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      value={values.address}
                    />
                  </div>
                </Grid>

                {/* City */}
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className='mb'>
                    <MaterialInputField
                      name='city'
                      id='city'
                      label='City *'
                      error={touched.city && errors.city}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      value={values.city}
                    />
                  </div>
                </Grid>

                {/* State / Province */}
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className='mb'>
                    <MaterialInputField
                      name='state'
                      id='state'
                      label='State / Province *'
                      error={touched.state && errors.state}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      value={values.state}
                    />
                  </div>
                </Grid>

                {/* Zip Code */}
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className='mb'>
                    <MaterialInputField
                      name='zipCode'
                      id='zipCode'
                      label='Postal / ZIP Code *'
                      error={touched.zipCode && errors.zipCode}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      value={values.zipCode}
                    />
                  </div>
                </Grid>

                {/* Country */}
                <Grid item xs={12} xl={6} lg={6} md={6}>
                  <div className='mb'>
                    <Box sx={{ minWidth: 120 }}>
                      <FormControl fullWidth>
                        <InputLabel id='country-label'>Country *</InputLabel>
                        <Select
                          sx={{
                            boxShadow: 'none',
                            '.MuiOutlinedInput-notchedOutline': { border: 0 },
                          }}
                          labelId='country-label'
                          id='country-select'
                          value={
                            countriesList.some(
                              (item) => item.value === values.country
                            )
                              ? values.country
                              : ''
                          }
                          label='Country *'
                          onChange={handleChange('country')}
                        >
                          {countriesList.map(({ value, label }, index) => (
                            <MenuItem key={index} value={value}>
                              {label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <div className='error-input-container'>
                      {errors.country && touched.country ? (
                        <p className='form-error'>{errors.country}</p>
                      ) : null}
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
          )}
        </Formik>
      }
      buttonText={loading ? null : 'Submit'}
      btnLoader={loading}
      handleClose={handleModalSubmit}
      customModalClass='post-signup-modal'
    />
  );
};

// Emergency Contact Modal (Updated UI)
// Emergency Contact Modal (Updated UI)
export const EmergencyModal = ({ show, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const formikRef = useRef(null);
  const userData = getSecondaryProfile();
  const {
    emergencyfields,
    setEmergencyFields,
    emergencyFieldCount,
    setEmergencyFieldCount,
  } = useContext(ApplicationContext);

  const handleAddMore = () => setEmergencyFieldCount(emergencyFieldCount + 1);

  const handleDeleteButtonClick = () => {
    if (emergencyFieldCount > 0) {
      setEmergencyFieldCount(emergencyFieldCount - 1);
      const newFields = { ...emergencyfields };
      const suffix = emergencyFieldCount;
      delete newFields[`name_${suffix}`];
      delete newFields[`phone_${suffix}`];
      delete newFields[`relation_${suffix}`];
      setEmergencyFields(newFields);
    }
  };

  const handleExtraFieldChange = (fieldName, value) => {
    setEmergencyFields({
      ...emergencyfields,
      [fieldName]: value,
    });
  };

  const isObjectEmpty = (object) => Object.keys(object).length === 0;

  const extractItemBeforeUnderscore = (data) => {
    return data.map((item) => {
      const result = {};
      for (const key in item) {
        const [itemName] = key.split('_');
        result[itemName] = item[key];
      }
      return result;
    });
  };

  // Custom validation function for Formik
  const validateForm = (values) => {
    const errors = {};

    // Validate main emergency contact
    if (!values.emergencyName || values.emergencyName.trim().length === 0) {
      errors.emergencyName = 'Name is required';
    } else if (values.emergencyName.trim().length < 3) {
      errors.emergencyName = 'Name must be at least 3 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(values.emergencyName)) {
      errors.emergencyName = 'Name can only contain letters and spaces';
    }

    if (!values.emergencyPhone || values.emergencyPhone.trim().length === 0) {
      errors.emergencyPhone = 'Phone number is required';
    } else {
      // Remove all non-digit characters
      const phoneDigits = values.emergencyPhone.replace(/\D/g, '');

      // Only check minimum length (7 digits)
      if (phoneDigits.length < 7) {
        errors.emergencyPhone = 'Phone number must have at least 7 digits';
      }
    }

    if (
      !values.emergencyRelation ||
      values.emergencyRelation.trim().length === 0
    ) {
      errors.emergencyRelation = 'Relationship is required';
    }

    // Validate extra emergency contacts using Formik's values
    for (let i = 1; i <= emergencyFieldCount; i++) {
      const nameField = `name_${i}`;
      const phoneField = `phone_${i}`;
      const relationField = `relation_${i}`;

      // Get values from emergencyfields context
      const nameValue = emergencyfields[nameField] || '';
      const phoneValue = emergencyfields[phoneField] || '';
      const relationValue = emergencyfields[relationField] || '';

      if (!nameValue || nameValue.trim().length === 0) {
        errors[nameField] = 'Name is required';
      } else if (nameValue.trim().length < 3) {
        errors[nameField] = 'Name must be at least 3 characters';
      } else if (!/^[a-zA-Z\s]+$/.test(nameValue)) {
        errors[nameField] = 'Name can only contain letters and spaces';
      }

      if (!phoneValue || phoneValue.trim().length === 0) {
        errors[phoneField] = 'Phone number is required';
      } else {
        // Remove all non-digit characters
        const phoneDigits = phoneValue.replace(/\D/g, '');

        // Only check minimum length (7 digits)
        if (phoneDigits.length < 7) {
          errors[phoneField] = 'Phone number must have at least 7 digits';
        }
      }

      if (!relationValue || relationValue.trim().length === 0) {
        errors[relationField] = 'Relationship is required';
      }
    }

    return errors;
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const profileId = getProfile()?.uid;
      const emergencyExtraFields = [];
      if (emergencyFieldCount > 0 && !isObjectEmpty(emergencyfields)) {
        emergencyExtraFields.push(emergencyfields);
      }

      let extra_fields =
        extractItemBeforeUnderscore(emergencyExtraFields) || [];
      if (Array.isArray(extra_fields)) {
        extra_fields = extra_fields.map((obj) => {
          let temp = {};
          Object.keys(obj).map((key) => {
            if (key.includes('name')) temp['name'] = obj[key];
            if (key.includes('phone')) temp['phone'] = obj[key];
            if (key.includes('relation')) temp['relationship'] = obj[key];
          });
          return temp;
        });
      }

      const emergencyContacts = [
        {
          name: values.emergencyName,
          phone: values.emergencyPhone,
          relationship: values.emergencyRelation,
        },
        ...extra_fields,
      ];

      const updatedData = { emergencyContact: emergencyContacts };

      await saveUserProfile({ profileId, ...updatedData });
      setSecondaryProfile({ ...userData, ...updatedData });
      setLoading(false);
      onComplete();
    } catch (error) {
      console.error('Error saving emergency contacts:', error);
      setLoading(false);
    }
  };

  const handleModalSubmit = () => {
    if (formikRef.current) {
      formikRef.current.setTouched({
        emergencyName: true,
        emergencyPhone: true,
        emergencyRelation: true,
      });
      // Also touch all extra fields
      const touchedFields = {
        emergencyName: true,
        emergencyPhone: true,
        emergencyRelation: true,
      };

      for (let i = 1; i <= emergencyFieldCount; i++) {
        touchedFields[`name_${i}`] = true;
        touchedFields[`phone_${i}`] = true;
        touchedFields[`relation_${i}`] = true;
      }

      formikRef.current.setTouched(touchedFields);
      formikRef.current.handleSubmit();
    }
  };

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    let phoneNumber = value.replace(/\D/g, '');

    // Add plus sign if not present and has digits
    if (phoneNumber.length > 0 && !value.startsWith('+')) {
      return '+' + phoneNumber;
    }

    return value;
  };

  return (
    <GenericModal
      show={show}
      type={ENUMS.MODAL_TYPES.INFO}
      title='Add Emergency Contacts'
      body={
        <Formik
          innerRef={formikRef}
          initialValues={{
            emergencyName: userData?.emergencyContact?.[0]?.name || '',
            emergencyPhone: userData?.emergencyContact?.[0]?.phone || '',
            emergencyRelation:
              userData?.emergencyContact?.[0]?.relationship || '',
          }}
          validate={validateForm}
          validateOnMount={false}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            handleChange,
            handleBlur,
            touched,
            errors,
            values,
            setFieldValue,
          }) => {
            // Combine Formik values with emergencyfields for validation
            const allErrors = { ...errors };
            console.log('emergencyfields', allErrors);
            // Custom handleChange for phone field to format it
            const customHandleChange = (fieldName) => (e) => {
              if (fieldName === 'emergencyPhone') {
                // const formattedValue = formatPhoneNumber(e.target.value);

                handleChange(fieldName)(e);
              }
            };

            return (
              <div className='post-signup-modal-content'>
                <Grid container spacing={0.7}>
                  {/* Main Emergency Contact */}
                  <Grid item xs={12}>
                    <MaterialInputField
                      name='emergencyName'
                      label='Name *'
                      value={values.emergencyName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.emergencyName && errors.emergencyName}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <PhoneInputField
                      name='emergencyPhone'
                      id='emergencyPhone'
                      placeholder='Phone * (e.g., +1234567890)'
                      value={values.emergencyPhone}
                      handleChange={handleChange('emergencyPhone')}
                      handleBlur={handleBlur('emergencyPhone')}
                      error={touched.emergencyPhone && errors.emergencyPhone}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Relationship *</InputLabel>
                      <Select
                        name='emergencyRelation'
                        value={values.emergencyRelation}
                        label='Relationship'
                        onChange={handleChange('emergencyRelation')}
                        onBlur={handleBlur('emergencyRelation')}
                        sx={{
                          borderRadius: '8px',
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: '1px solid #ccc',
                          },
                        }}
                        error={
                          touched.emergencyRelation &&
                          Boolean(errors.emergencyRelation)
                        }
                      >
                        {emergencyRelationshipList.map(({ value, label }) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.emergencyRelation &&
                        touched.emergencyRelation && (
                          <p className='form-error'>
                            {errors.emergencyRelation}
                          </p>
                        )}
                    </FormControl>
                  </Grid>

                  {/* Extra Emergency Contacts */}
                  {Array.from({ length: emergencyFieldCount }, (_, index) => {
                    const suffix = index + 1;
                    const nameField = `name_${suffix}`;
                    const phoneField = `phone_${suffix}`;
                    const relationField = `relation_${suffix}`;

                    // Check if fields are touched (we'll manage this manually)
                    const isNameTouched = touched[nameField] || false;
                    const isPhoneTouched = touched[phoneField] || false;
                    const isRelationTouched = touched[relationField] || false;

                    return (
                      <React.Fragment key={index}>
                        <Grid item xs={12}>
                          <Box
                            display='flex'
                            justifyContent='space-between'
                            alignItems='center'
                            mt={2}
                          >
                            <Typography fontWeight={600}>
                              Emergency Contact {suffix + 1}
                            </Typography>
                            {index === emergencyFieldCount - 1 && (
                              <Button
                                color='error'
                                onClick={handleDeleteButtonClick}
                                size='small'
                                sx={{ textTransform: 'none' }}
                              >
                                Remove
                              </Button>
                            )}
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <MaterialInputField
                            name={nameField}
                            label='Name *'
                            value={emergencyfields[nameField] || ''}
                            onChange={(e) =>
                              handleExtraFieldChange(nameField, e.target.value)
                            }
                            onBlur={() => {
                              // Manually set touched for extra fields
                              if (formikRef.current) {
                                formikRef.current.setTouched({
                                  ...formikRef.current.touched,
                                  [nameField]: true,
                                });
                              }
                            }}
                            error={isNameTouched && errors[nameField]}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <PhoneInputField
                            name={phoneField}
                            placeholder='Phone * (e.g., +1234567890)'
                            value={emergencyfields[phoneField] || ''}
                            handleChange={(value) => {
                              // const formattedValue = formatPhoneNumber(value);
                              handleExtraFieldChange(phoneField, value);
                            }}
                            handleBlur={() => {
                              // Manually set touched for extra fields
                              if (formikRef.current) {
                                formikRef.current.setTouched({
                                  ...formikRef.current.touched,
                                  [phoneField]: true,
                                });
                              }
                            }}
                            error={isPhoneTouched && errors[phoneField]}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel>Relationship *</InputLabel>
                            <Select
                              name={relationField}
                              value={emergencyfields[relationField] || ''}
                              onChange={(e) =>
                                handleExtraFieldChange(
                                  relationField,
                                  e.target.value
                                )
                              }
                              onBlur={() => {
                                // Manually set touched for extra fields
                                if (formikRef.current) {
                                  formikRef.current.setTouched({
                                    ...formikRef.current.touched,
                                    [relationField]: true,
                                  });
                                }
                              }}
                              sx={{
                                borderRadius: '8px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  border: '1px solid #ccc',
                                },
                              }}
                              error={
                                isRelationTouched &&
                                Boolean(errors[relationField])
                              }
                            >
                              {emergencyRelationshipList.map(
                                ({ value, label }) => (
                                  <MenuItem key={value} value={value}>
                                    {label}
                                  </MenuItem>
                                )
                              )}
                            </Select>
                            {errors[relationField] && isRelationTouched && (
                              <p className='form-error'>
                                {errors[relationField]}
                              </p>
                            )}
                          </FormControl>
                        </Grid>
                      </React.Fragment>
                    );
                  })}

                  {/* Add Another Button */}
                  <Grid item xs={12}>
                    <Button
                      variant='outlined'
                      fullWidth
                      onClick={handleAddMore}
                      sx={{
                        borderStyle: 'dashed',
                        borderWidth: '2px',
                        color: '#666',
                        fontWeight: 600,
                        mt: 1,
                        py: 1.2,
                        borderRadius: '8px',
                        textTransform: 'none',
                      }}
                    >
                      + Add Another Emergency Contact
                    </Button>
                  </Grid>
                </Grid>
              </div>
            );
          }}
        </Formik>
      }
      buttonText={loading ? null : 'Submit'}
      btnLoader={loading}
      handleClose={handleModalSubmit}
      customModalClass='post-signup-modal'
    />
  );
};

export const InsuranceModal = ({ show, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const formikRef = useRef(null);
  const userData = getSecondaryProfile();
  const {
    insurancefields,
    setInsuranceFields,
    insuranceFieldCount,
    setInsuranceFieldCount,
  } = useContext(ApplicationContext);

  const handleAddMore = () => {
    setInsuranceFieldCount(insuranceFieldCount + 1);
  };

  const handleDeleteButtonClick = () => {
    if (insuranceFieldCount > 0) {
      setInsuranceFieldCount(insuranceFieldCount - 1);
      const newFields = { ...insurancefields };
      const suffix = insuranceFieldCount;
      delete newFields[`type_${suffix}`];
      delete newFields[`company_${suffix}`];
      delete newFields[`policy_${suffix}`];
      delete newFields[`phone_${suffix}`];
      setInsuranceFields(newFields);
    }
  };

  const handleExtraFieldChange = (fieldName, value) => {
    setInsuranceFields({
      ...insurancefields,
      [fieldName]: value,
    });
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const profileId = getProfile()?.uid;

      let extra = [];
      if (
        insuranceFieldCount > 0 &&
        Object.keys(insurancefields || {}).length > 0
      ) {
        const grouped = [];
        for (let i = 1; i <= insuranceFieldCount; i++) {
          const obj = {};
          if (insurancefields[`type_${i}`])
            obj.type = insurancefields[`type_${i}`];
          if (insurancefields[`company_${i}`])
            obj.company = insurancefields[`company_${i}`];
          if (insurancefields[`policy_${i}`])
            obj.policy = insurancefields[`policy_${i}`];
          if (insurancefields[`phone_${i}`])
            obj.phone = insurancefields[`phone_${i}`];
          if (Object.keys(obj).length > 0) grouped.push(obj);
        }
        extra = grouped;
      }

      const insurances = [];
      const hasMainValues =
        (values.insuranceType && values.insuranceType !== 'Choose') ||
        (values.insuranceCompany && values.insuranceCompany.trim() !== '') ||
        (values.policyNumber && values.policyNumber.trim() !== '') ||
        (values.emergencyNumber && values.emergencyNumber.trim() !== '');
      if (hasMainValues) {
        insurances.push({
          type: values.insuranceType === 'Choose' ? '' : values.insuranceType,
          company: values.insuranceCompany || '',
          policy: values.policyNumber || '',
          phone: values.emergencyNumber || '',
        });
      }
      const allIns = [...insurances, ...extra];

      await saveUserProfile({
        profileId,
        insurance: allIns,
      });

      const updatedProfile = { ...userData, insurance: allIns };
      setSecondaryProfile(updatedProfile);
      setLoading(false);
      onComplete();
    } catch (error) {
      console.error('Error saving insurance:', error);
      setLoading(false);
    }
  };

  const handleModalSubmit = () => {
    if (formikRef.current) {
      // If user hasn't entered any main values and there are no extra entries, skip saving and continue
      const values = formikRef.current.values || {};
      const hasMainValues =
        (values.insuranceType && values.insuranceType !== '') ||
        (values.insuranceCompany && values.insuranceCompany.trim() !== '') ||
        (values.policyNumber && values.policyNumber.trim() !== '') ||
        (values.emergencyNumber && values.emergencyNumber.trim() !== '');
      const hasExtraValues =
        insuranceFieldCount > 0 &&
        Object.keys(insurancefields || {}).length > 0 &&
        (() => {
          for (let i = 1; i <= insuranceFieldCount; i++) {
            if (
              insurancefields[`type_${i}`] ||
              insurancefields[`company_${i}`] ||
              insurancefields[`policy_${i}`] ||
              insurancefields[`phone_${i}`]
            )
              return true;
          }
          return false;
        })();

      if (!hasMainValues && !hasExtraValues) {
        onComplete && onComplete(true);
        return;
      }

      formikRef.current.handleSubmit();
    }
  };

  return (
    <GenericModal
      show={show}
      type={ENUMS.MODAL_TYPES.INFO}
      title='Add Insurance Information (optional)'
      body={
        <Formik
          innerRef={formikRef}
          initialValues={{
            insuranceType: userData.insuranceType || '',
            insuranceCompany: userData.insuranceCompany || '',
            policyNumber: userData.policyNumber || '',
            emergencyNumber: userData.emergencyNumber || '',
          }}
          validateOnMount={false}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            handleChange,
            handleBlur,
            values,
            setFieldValue,
            setFieldTouched,
          }) => (
            <div className='post-signup-modal-content'>
              <Grid container spacing={0.7}>
                {/* Main insurance fields */}
                <Grid item xs={12} xl={12} lg={12} md={12}>
                  <Box sx={{ minWidth: 120 }}>
                    <FormControl fullWidth>
                      <InputLabel id='insurance-type-label'>
                        Type of Insurance
                      </InputLabel>
                      <Select
                        sx={{
                          borderRadius: '8px',
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: '1px solid #ccc',
                          },
                        }}
                        labelId='insurance-type-label'
                        id='insurance-type-select'
                        value={values.insuranceType}
                        onChange={handleChange('insuranceType')}
                      >
                        <MenuItem value={'Health'}>Health</MenuItem>
                        <MenuItem value={'Travel'}>Travel</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>

                <Grid item xs={12} xl={12} lg={12} md={12}>
                  <div className='mb'>
                    <MaterialInputField
                      name='insuranceCompany'
                      id='insuranceCompany'
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      label='Insurance Company'
                      value={values.insuranceCompany}
                    />
                  </div>
                </Grid>

                <Grid item xs={12} xl={12} lg={12} md={12}>
                  <div className='mb'>
                    <MaterialInputField
                      name='policyNumber'
                      id='policyNumber'
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      label='Policy Number'
                      value={values.policyNumber}
                    />
                  </div>
                </Grid>

                <Grid item xs={12} xl={12} lg={12} md={12}>
                  <div className='mb'>
                    <PhoneInputField
                      name='emergencyNumber'
                      id='emergencyNumber'
                      placeholder='Contact Phone'
                      value={values.emergencyNumber}
                      handleChange={handleChange('emergencyNumber')}
                      handleBlur={handleBlur('emergencyNumber')}
                    />
                  </div>
                </Grid>

                {/* Additional insurance entries */}
                {Array.from({ length: insuranceFieldCount }, (_, index) => {
                  const suffix = index + 1;
                  const labelId = `insurance-extra-type-label-${suffix}`;
                  const selectId = `insurance-extra-type-select-${suffix}`;
                  return (
                    <React.Fragment key={index}>
                      <Grid item xs={12}>
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 700,
                            marginTop: 10,
                            marginBottom: 5,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <span>{`Insurance ${suffix + 1}`}</span>
                          <button
                            type='button'
                            onClick={handleDeleteButtonClick}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#d32f2f',
                              fontWeight: '600',
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </Grid>

                      <Grid item xs={12} xl={12} lg={12} md={12}>
                        <Box sx={{ minWidth: 120 }}>
                          <FormControl fullWidth>
                            <InputLabel id={labelId}>
                              Type of Insurance
                            </InputLabel>
                            <Select
                              labelId={labelId}
                              id={selectId}
                              value={insurancefields[`type_${suffix}`] || ''}
                              onChange={(e) =>
                                handleExtraFieldChange(
                                  `type_${suffix}`,
                                  e.target.value
                                )
                              }
                              sx={{
                                borderRadius: '8px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  border: '1px solid #ccc',
                                },
                              }}
                            >
                              <MenuItem value={'Health'}>Health</MenuItem>
                              <MenuItem value={'Travel'}>Travel</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Grid>

                      <Grid item xs={12} xl={12} lg={12} md={12}>
                        <MaterialInputField
                          name={`company_${suffix}`}
                          id={`company_${suffix}`}
                          handleChange={(e) =>
                            handleExtraFieldChange(
                              `company_${suffix}`,
                              e.target.value
                            )
                          }
                          label='Company'
                          value={insurancefields[`company_${suffix}`] || ''}
                        />
                      </Grid>

                      <Grid item xs={12} xl={12} lg={12} md={12}>
                        <MaterialInputField
                          name={`policy_${suffix}`}
                          id={`policy_${suffix}`}
                          handleChange={(e) =>
                            handleExtraFieldChange(
                              `policy_${suffix}`,
                              e.target.value
                            )
                          }
                          label='Policy'
                          value={insurancefields[`policy_${suffix}`] || ''}
                        />
                      </Grid>

                      <Grid item xs={12} xl={12} lg={12} md={12}>
                        <PhoneInputField
                          name={`phone_${suffix}`}
                          id={`phone_${suffix}`}
                          placeholder='Phone'
                          value={insurancefields[`phone_${suffix}`] || ''}
                          handleChange={(val) =>
                            handleExtraFieldChange(`phone_${suffix}`, val)
                          }
                        />
                      </Grid>
                    </React.Fragment>
                  );
                })}

                <Grid item xs={12}>
                  <button
                    type='button'
                    onClick={handleAddMore}
                    style={{
                      width: '100%',
                      padding: '12px',
                      marginTop: 10,
                      background: 'transparent',
                      border: '2px dashed #bdbdbd',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      color: '#616161',
                      fontWeight: '600',
                      fontSize: 15,
                      transition: '0.2s ease',
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.borderColor = '#9e9e9e')
                    }
                    onMouseOut={(e) => (e.target.style.borderColor = '#bdbdbd')}
                  >
                    + Add Another Insurance
                  </button>
                </Grid>
              </Grid>
            </div>
          )}
        </Formik>
      }
      buttonText={loading ? null : 'Save & Continue'}
      btnLoader={loading}
      handleClose={handleModalSubmit}
      customModalClass='post-signup-modal'
    />
  );
};
